import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  AllowanceTypeApi, CreateAllowanceTypePayload, EditAllowanceTypePayload,
  ContributionSettingApi, CreateContributionSettingPayload, EditContributionSettingPayload,
  DeductionTypeApi, CreateDeductionTypePayload, EditDeductionTypePayload,
  PayrollPeriodApi, CreatePayrollPeriodPayload, EditPayrollPeriodPayload,
  PayrollScheduleApi, CreatePayrollSchedulePayload, EditPayrollSchedulePayload,
  TaxBracketApi, CreateTaxBracketPayload, EditTaxBracketPayload,
} from '../../models/payroll/payroll-config.model';
import {
  PayrollRunApi, CreatePayrollRunPayload, PayrollResultApi, PayslipApi, EmployeeDtrStatusApi,
  PayrollSkipEmployeeApi, PayrollSkipItemPayload, PayrollEarningAdjustmentPayload,
} from '../../models/payroll/payroll-run.model';
import {
  EmployeeDeductionApi, CreateEmployeeDeductionPayload, EditEmployeeDeductionPayload,
} from '../../models/payroll/employee-deduction.model';
import {
  EmployeeAllowanceApi, CreateEmployeeAllowancePayload, EditEmployeeAllowancePayload,
} from '../../models/payroll/employee-allowance.model';
import {
  ComputeBatchThirteenthMonthPayload,
  CreateThirteenthMonthPayrollPayload,
  ThirteenthMonthResultApi,
} from '../../models/payroll/thirteenth-month.model';
import {
  ComputeBatchFinalPayPayload,
  CreateFinalPayPayrollPayload,
  FinalPayResultApi,
} from '../../models/payroll/final-pay.model';
import {
  ComputeSalaryAdjustmentPayload,
  CreateSalaryAdjustmentPayrollPayload,
  SalaryAdjustmentPreviewApi,
} from '../../models/payroll/salary-adjustment.model';
import {
  BankExportProfileApi,
  CreateBankExportProfilePayload,
  EditBankExportProfilePayload,
} from '../../models/payroll/bank-export-profile.model';
import {
  PayslipFieldFlags,
  PayslipLayout,
  PayslipTemplate,
} from '../../models/payslip/payslip-template.model';

export interface UpsertPayslipTemplatePayload {
  layout: PayslipLayout;
  fields: PayslipFieldFlags;
  headerText: string | null;
  footerText: string | null;
}

const OPT = { withCredentials: true } as const;

@Injectable({ providedIn: 'root' })
export class PayrollService {
  constructor(private http: HttpClient) {}

  // ── Payroll Schedules ─────────────────────────────────────────────────────

  getSchedules(): Observable<{ payrollSchedules: PayrollScheduleApi[] }> {
    return this.http.get<{ payrollSchedules: PayrollScheduleApi[] }>(
      environment.payroll.schedules, OPT,
    );
  }

  createSchedule(payload: CreatePayrollSchedulePayload): Observable<{ payrollSchedule: PayrollScheduleApi }> {
    return this.http.post<{ payrollSchedule: PayrollScheduleApi }>(
      environment.payroll.schedules, payload, OPT,
    );
  }

  editSchedule(payload: EditPayrollSchedulePayload): Observable<{ payrollSchedule: PayrollScheduleApi }> {
    return this.http.put<{ payrollSchedule: PayrollScheduleApi }>(
      environment.payroll.schedules, payload, OPT,
    );
  }

  deleteSchedule(guid: string): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(
      `${environment.payroll.schedules}/${guid}`, OPT,
    );
  }

  // ── Payroll Periods ───────────────────────────────────────────────────────

  getPeriods(payrollScheduleId?: number): Observable<{ payrollPeriods: PayrollPeriodApi[] }> {
    const params: Record<string, string> = {};
    if (payrollScheduleId != null) params['payrollScheduleId'] = payrollScheduleId.toString();
    return this.http.get<{ payrollPeriods: PayrollPeriodApi[] }>(
      environment.payroll.periods, { ...OPT, params },
    );
  }

  createPeriod(payload: CreatePayrollPeriodPayload): Observable<{ payrollPeriod: PayrollPeriodApi }> {
    return this.http.post<{ payrollPeriod: PayrollPeriodApi }>(
      environment.payroll.periods, payload, OPT,
    );
  }

  editPeriod(payload: EditPayrollPeriodPayload): Observable<{ payrollPeriod: PayrollPeriodApi }> {
    return this.http.put<{ payrollPeriod: PayrollPeriodApi }>(
      environment.payroll.periods, payload, OPT,
    );
  }

  deletePeriod(guid: string): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(
      `${environment.payroll.periods}/${guid}`, OPT,
    );
  }

  // ── Allowance Types ───────────────────────────────────────────────────────

  getAllowanceTypes(): Observable<{ allowanceTypes: AllowanceTypeApi[] }> {
    return this.http.get<{ allowanceTypes: AllowanceTypeApi[] }>(
      environment.payroll.allowanceTypes, OPT,
    );
  }

  createAllowanceType(payload: CreateAllowanceTypePayload): Observable<{ allowanceType: AllowanceTypeApi }> {
    return this.http.post<{ allowanceType: AllowanceTypeApi }>(
      environment.payroll.allowanceTypes, payload, OPT,
    );
  }

  editAllowanceType(payload: EditAllowanceTypePayload): Observable<{ allowanceType: AllowanceTypeApi }> {
    return this.http.put<{ allowanceType: AllowanceTypeApi }>(
      environment.payroll.allowanceTypes, payload, OPT,
    );
  }

  deleteAllowanceType(guid: string): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(
      `${environment.payroll.allowanceTypes}/${guid}`, OPT,
    );
  }

  // ── Deduction Types ───────────────────────────────────────────────────────

  getDeductionTypes(): Observable<{ deductionTypes: DeductionTypeApi[] }> {
    return this.http.get<{ deductionTypes: DeductionTypeApi[] }>(
      environment.payroll.deductionTypes, OPT,
    );
  }

  createDeductionType(payload: CreateDeductionTypePayload): Observable<{ deductionType: DeductionTypeApi }> {
    return this.http.post<{ deductionType: DeductionTypeApi }>(
      environment.payroll.deductionTypes, payload, OPT,
    );
  }

  editDeductionType(payload: EditDeductionTypePayload): Observable<{ deductionType: DeductionTypeApi }> {
    return this.http.put<{ deductionType: DeductionTypeApi }>(
      environment.payroll.deductionTypes, payload, OPT,
    );
  }

  deleteDeductionType(guid: string): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(
      `${environment.payroll.deductionTypes}/${guid}`, OPT,
    );
  }

  // ── Contribution Settings ─────────────────────────────────────────────────

  getContributionSettings(): Observable<{ contributionSettings: ContributionSettingApi[] }> {
    return this.http.get<{ contributionSettings: ContributionSettingApi[] }>(
      environment.payroll.contributionSettings, OPT,
    );
  }

  createContributionSetting(
    payload: CreateContributionSettingPayload,
  ): Observable<{ contributionSetting: ContributionSettingApi }> {
    return this.http.post<{ contributionSetting: ContributionSettingApi }>(
      environment.payroll.contributionSettings, payload, OPT,
    );
  }

  editContributionSetting(
    payload: EditContributionSettingPayload,
  ): Observable<{ contributionSetting: ContributionSettingApi }> {
    return this.http.put<{ contributionSetting: ContributionSettingApi }>(
      environment.payroll.contributionSettings, payload, OPT,
    );
  }

  deleteContributionSetting(guid: string): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(
      `${environment.payroll.contributionSettings}/${guid}`, OPT,
    );
  }

  // ── Tax Brackets ──────────────────────────────────────────────────────────

  getTaxBrackets(effectiveYear?: number): Observable<{ taxBrackets: TaxBracketApi[] }> {
    const params: Record<string, string> = {};
    if (effectiveYear != null) params['effectiveYear'] = effectiveYear.toString();
    return this.http.get<{ taxBrackets: TaxBracketApi[] }>(
      environment.payroll.taxBrackets, { ...OPT, params },
    );
  }

  createTaxBracket(payload: CreateTaxBracketPayload): Observable<{ taxBracket: TaxBracketApi }> {
    return this.http.post<{ taxBracket: TaxBracketApi }>(
      environment.payroll.taxBrackets, payload, OPT,
    );
  }

  editTaxBracket(payload: EditTaxBracketPayload): Observable<{ taxBracket: TaxBracketApi }> {
    return this.http.put<{ taxBracket: TaxBracketApi }>(
      environment.payroll.taxBrackets, payload, OPT,
    );
  }

  deleteTaxBracket(guid: string): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(
      `${environment.payroll.taxBrackets}/${guid}`, OPT,
    );
  }

  // ── Payroll Runs ──────────────────────────────────────────────────────────

  getPayrollRuns(payrollPeriodId?: number): Observable<{ payrollRuns: PayrollRunApi[] }> {
    const params: Record<string, string> = {};
    if (payrollPeriodId != null) params['payrollPeriodId'] = payrollPeriodId.toString();
    return this.http.get<{ payrollRuns: PayrollRunApi[] }>(
      environment.payroll.runs, { ...OPT, params },
    );
  }

  getPayrollRunByGuid(guid: string): Observable<{ payrollRun: PayrollRunApi }> {
    return this.http.get<{ payrollRun: PayrollRunApi }>(
      `${environment.payroll.runs}/${guid}`, OPT,
    );
  }

  createPayrollRun(payload: CreatePayrollRunPayload): Observable<{ payrollRun: PayrollRunApi }> {
    return this.http.post<{ payrollRun: PayrollRunApi }>(
      environment.payroll.runs, payload, OPT,
    );
  }

  getPayrollResults(runId: number): Observable<{ payrollResults: PayrollResultApi[] }> {
    return this.http.get<{ payrollResults: PayrollResultApi[] }>(
      `${environment.payroll.runs}/${runId}/results`, OPT,
    );
  }

  getDeductionSkips(payrollRunGuid: string): Observable<{ employees: PayrollSkipEmployeeApi[] }> {
    return this.http.get<{ employees: PayrollSkipEmployeeApi[] }>(
      `${environment.payroll.runs}/${payrollRunGuid}/deduction-skips`, OPT,
    );
  }

  saveDeductionSkips(
    payrollRunGuid: string,
    skips: PayrollSkipItemPayload[],
    earnings: PayrollEarningAdjustmentPayload[],
  ): Observable<{ saved: boolean }> {
    return this.http.put<{ saved: boolean }>(
      `${environment.payroll.runs}/${payrollRunGuid}/deduction-skips`,
      { skips, earnings },
      OPT,
    );
  }

  processPayrollRun(guid: string): Observable<{ payrollRun: PayrollRunApi }> {
    return this.http.post<{ payrollRun: PayrollRunApi }>(
      `${environment.payroll.runs}/${guid}/process`, {}, OPT,
    );
  }

  unlockPayrollRun(guid: string): Observable<{ payrollRun: PayrollRunApi }> {
    return this.http.post<{ payrollRun: PayrollRunApi }>(
      `${environment.payroll.runs}/${guid}/unlock`, {}, OPT,
    );
  }

  deletePayrollRun(guid: string): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(
      `${environment.payroll.runs}/${guid}`, OPT,
    );
  }

  getPayslips(runId: number): Observable<{ payslips: PayslipApi[] }> {
    return this.http.get<{ payslips: PayslipApi[] }>(
      `${environment.payroll.runs}/${runId}/payslips`, OPT,
    );
  }

  downloadAccountingJournal(runGuid: string, format: 'csv' | 'xlsx' = 'csv'): Observable<Blob> {
    return this.http.get(
      `${environment.payroll.runs}/${runGuid}/accounting-journal`,
      { ...OPT, params: { format }, responseType: 'blob' },
    );
  }

  downloadBankDisbursement(
    runGuid: string,
    format: 'csv' | 'xlsx' = 'csv',
    profileGuid?: string | null,
  ): Observable<Blob> {
    const params: Record<string, string> = { format };
    if (profileGuid) params['profileGuid'] = profileGuid;
    return this.http.get(
      `${environment.payroll.runs}/${runGuid}/bank-disbursement`,
      { ...OPT, params, responseType: 'blob' },
    );
  }

  getBankExportProfiles(): Observable<{ profiles: BankExportProfileApi[] }> {
    return this.http.get<{ profiles: BankExportProfileApi[] }>(
      environment.payroll.bankExportProfiles, OPT,
    );
  }

  getBankExportSourceFields(): Observable<{ sourceFields: string[] }> {
    return this.http.get<{ sourceFields: string[] }>(
      `${environment.payroll.bankExportProfiles}/source-fields`, OPT,
    );
  }

  createBankExportProfile(payload: CreateBankExportProfilePayload): Observable<{ profile: BankExportProfileApi }> {
    return this.http.post<{ profile: BankExportProfileApi }>(
      environment.payroll.bankExportProfiles, payload, OPT,
    );
  }

  editBankExportProfile(
    profileGuid: string,
    payload: EditBankExportProfilePayload,
  ): Observable<{ profile: BankExportProfileApi }> {
    return this.http.put<{ profile: BankExportProfileApi }>(
      `${environment.payroll.bankExportProfiles}/${profileGuid}`, payload, OPT,
    );
  }

  deleteBankExportProfile(profileGuid: string): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(
      `${environment.payroll.bankExportProfiles}/${profileGuid}`, OPT,
    );
  }

  triggerDownload(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  getDtrStatusByPeriod(periodId: number): Observable<{ dtrStatuses: EmployeeDtrStatusApi[] }> {
    return this.http.get<{ dtrStatuses: EmployeeDtrStatusApi[] }>(
      `${environment.payroll.runs}/dtr-status`, { ...OPT, params: { periodId: periodId.toString() } },
    );
  }

  // ── Employee Deductions ───────────────────────────────────────────────────

  getEmployeeDeductions(employeeId?: number): Observable<{ employeeDeductions: EmployeeDeductionApi[] }> {
    const params: Record<string, string> = {};
    if (employeeId != null) params['employeeId'] = employeeId.toString();
    return this.http.get<{ employeeDeductions: EmployeeDeductionApi[] }>(
      environment.payroll.employeeDeductions, { ...OPT, params },
    );
  }

  createEmployeeDeduction(
    payload: CreateEmployeeDeductionPayload,
  ): Observable<{ employeeDeduction: EmployeeDeductionApi }> {
    return this.http.post<{ employeeDeduction: EmployeeDeductionApi }>(
      environment.payroll.employeeDeductions, payload, OPT,
    );
  }

  editEmployeeDeduction(
    payload: EditEmployeeDeductionPayload,
  ): Observable<{ employeeDeduction: EmployeeDeductionApi }> {
    return this.http.put<{ employeeDeduction: EmployeeDeductionApi }>(
      environment.payroll.employeeDeductions, payload, OPT,
    );
  }

  deleteEmployeeDeduction(guid: string): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(
      `${environment.payroll.employeeDeductions}/${guid}`, OPT,
    );
  }

  // ── Employee Allowances ──────────────────────────────────────────────────

  getEmployeeAllowances(employeeId?: number): Observable<{ employeeAllowances: EmployeeAllowanceApi[] }> {
    const params: Record<string, string> = {};
    if (employeeId != null) params['employeeId'] = employeeId.toString();
    return this.http.get<{ employeeAllowances: EmployeeAllowanceApi[] }>(
      environment.payroll.employeeAllowances, { ...OPT, params },
    );
  }

  createEmployeeAllowance(
    payload: CreateEmployeeAllowancePayload,
  ): Observable<{ employeeAllowance: EmployeeAllowanceApi }> {
    return this.http.post<{ employeeAllowance: EmployeeAllowanceApi }>(
      environment.payroll.employeeAllowances, payload, OPT,
    );
  }

  editEmployeeAllowance(
    payload: EditEmployeeAllowancePayload,
  ): Observable<{ employeeAllowance: EmployeeAllowanceApi }> {
    return this.http.put<{ employeeAllowance: EmployeeAllowanceApi }>(
      environment.payroll.employeeAllowances, payload, OPT,
    );
  }

  deleteEmployeeAllowance(guid: string): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(
      `${environment.payroll.employeeAllowances}/${guid}`, OPT,
    );
  }

  // ── 13th Month ────────────────────────────────────────────────────────────

  computeBatchThirteenthMonth(
    payload: ComputeBatchThirteenthMonthPayload,
  ): Observable<{ results: ThirteenthMonthResultApi[] }> {
    return this.http.post<{ results: ThirteenthMonthResultApi[] }>(
      `${environment.payroll.thirteenthMonth}/compute-batch`, payload, OPT,
    );
  }

  createThirteenthMonthPayroll(
    payload: CreateThirteenthMonthPayrollPayload,
  ): Observable<{ payrollRun: PayrollRunApi }> {
    return this.http.post<{ payrollRun: PayrollRunApi }>(
      `${environment.payroll.thirteenthMonth}/payroll`, payload, OPT,
    );
  }

  // ── Final Pay ─────────────────────────────────────────────────────────────

  computeBatchFinalPay(
    payload: ComputeBatchFinalPayPayload,
  ): Observable<{ results: FinalPayResultApi[] }> {
    return this.http.post<{ results: FinalPayResultApi[] }>(
      `${environment.payroll.finalPay}/compute-batch`, payload, OPT,
    );
  }

  createFinalPayPayroll(
    payload: CreateFinalPayPayrollPayload,
  ): Observable<{ payrollRun: PayrollRunApi }> {
    return this.http.post<{ payrollRun: PayrollRunApi }>(
      `${environment.payroll.finalPay}/payroll`, payload, OPT,
    );
  }

  // ── Salary Adjustment ─────────────────────────────────────────────────────

  computeSalaryAdjustment(
    payload: ComputeSalaryAdjustmentPayload,
  ): Observable<{ results: SalaryAdjustmentPreviewApi[] }> {
    return this.http.post<{ results: SalaryAdjustmentPreviewApi[] }>(
      `${environment.payroll.salaryAdjustments}/compute`, payload, OPT,
    );
  }

  createSalaryAdjustmentPayroll(
    payload: CreateSalaryAdjustmentPayrollPayload,
  ): Observable<{ payrollRun: PayrollRunApi }> {
    return this.http.post<{ payrollRun: PayrollRunApi }>(
      `${environment.payroll.salaryAdjustments}/payroll`, payload, OPT,
    );
  }

  // ── Payslip template ──────────────────────────────────────────────────────

  getPayslipTemplate(): Observable<{ template: PayslipTemplate }> {
    return this.http.get<{ template: PayslipTemplate }>(
      environment.payroll.payslipTemplate, OPT,
    );
  }

  upsertPayslipTemplate(payload: UpsertPayslipTemplatePayload): Observable<{ template: PayslipTemplate }> {
    return this.http.put<{ template: PayslipTemplate }>(
      environment.payroll.payslipTemplate, { template: payload }, OPT,
    );
  }

  uploadPayslipLogo(file: File): Observable<{ template: PayslipTemplate }> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<{ template: PayslipTemplate }>(
      `${environment.payroll.payslipTemplate}/logo`, form, OPT,
    );
  }

  clearPayslipLogo(): Observable<{ template: PayslipTemplate }> {
    return this.http.delete<{ template: PayslipTemplate }>(
      `${environment.payroll.payslipTemplate}/logo`, OPT,
    );
  }
}
